import OrgPayment from "@/atomic/organisms/orgPayment";
import Icon from "@/atomic/atoms/Icon";
import DebtPassageCard from "@/atomic/molecules/DebtPassageCard";
import PassagesCountStatus from "@/atomic/molecules/PassagesCountStatus";
import { areAllSelectableDebtsSelected } from "@/utils/transform";
import { PropsView } from "./Models";
import LoadingOverlay from "@/atomic/atoms/LoadingOverlay";
import useI18n from "@/hooks/useI18n";
import LanguageSelector from "@/atomic/atoms/LanguageSelector";
import Modal from "@/atomic/atoms/Modal";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import WebViewMenu from "@/atomic/molecules/WebViewMenu";
import environment from "@/settings/environment";
import Spinner from "@/atomic/atoms/Spinner";
import EmptyConsultationResult from "@/atomic/organisms/EmptyConsultationResult";
import PartnerPassagesResult from "@/atomic/organisms/PartnerPassagesResult";
import Alert from "@/atomic/atoms/Alert";
import SheetBackdrop from "@/atomic/atoms/SheetBackdrop";
import { createPendingConsultation } from "@/domain/scrapers/consultation";
export default function HomeView({
  goToVehicles,
  items,
  plate,
  isLoading,
  onChange,
  selectables,
  goGetout,
  onSearchNewPlate,
  scrapersConsultation,
  isDefaultFlow = true,
  isCaptchaVerified = false,
  isCaptchaMtcCaptcha = false,
  captchaName = "mtcaptcha-home-modal",
  resetCaptcha,
  incrementCaptchaKey,
  isLoadingCaptchaFlag = false,
  showAvulsoBackdrop = false,
  avulsoPassageCount = 0,
  avulsoTotalValue = "0,00",
  onAvulsoConfirm,
  onAvulsoDeny,
  isWebView = false,
  showNoDataBackdrop = false,
  onNoDataRegister,
  onNoDataDismiss,
  showTagBackdrop = false,
  onTagContract,
  onTagDismiss,
  showBeforeLeaveBackdrop = false,
  onBeforeLeavePayNow,
  onBeforeLeaveDismiss,
}: PropsView) {
  const { t } = useI18n();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPlate, setNewPlate] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isWebViewMenuOpen, setIsWebViewMenuOpen] = useState(false);
  const hasInitializedCaptcha = useRef(false);

  const formattedPlate = useMemo(
    () =>
      plate
        ? plate.replace(/^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/, "$1-$2")
        : "",
    [plate],
  );

  const isButtonDisabled = useMemo(
    () =>
      !newPlate ||
      newPlate.replace(/[^A-Z0-9]/g, "").length < 5 ||
      !isCaptchaVerified,
    [newPlate, isCaptchaVerified],
  );

  const buttonClassName = useMemo(
    () =>
      `w-full h-[calc(60px)] rounded-theme-medium text-white font-medium ${
        isButtonDisabled
          ? "bg-disabled text-disabled-text cursor-not-allowed"
          : "bg-primary hover:opacity-90"
      }`,
    [isButtonDisabled],
  );

  const handleSearchClick = useCallback(() => {
    const cleanPlate = newPlate.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (cleanPlate && cleanPlate.length >= 5 && onSearchNewPlate) {
      onSearchNewPlate(cleanPlate);
      setIsModalOpen(false);
      setNewPlate("");
    }
  }, [newPlate, onSearchNewPlate]);

  const toggleModal = useCallback(() => {
    const wasOpen = isModalOpen;
    setIsModalOpen(!isModalOpen);

    if (wasOpen) {
      setShowCaptcha(false);
      setNewPlate("");

      if (isCaptchaMtcCaptcha) {
        const element = document.getElementById(captchaName);
        if (element) {
          element.remove();
        }
        if ((window as any)?.mtcaptcha?.renderQueue) {
          const queue = (window as any).mtcaptcha.renderQueue;
          const index = queue.indexOf(captchaName);
          if (index > -1) {
            queue.splice(index, 1);
          }
        }
        try {
          if ((window as any)?.mtcaptcha?.resetUI) {
            (window as any).mtcaptcha.resetUI(captchaName);
          }
        } catch (error) {}
      } else {
        const recaptchaContainer = document.querySelector(".g-recaptcha");
        if (recaptchaContainer?.parentElement) {
          recaptchaContainer.parentElement.innerHTML = "";
        }
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        } else if ((window as any).grecaptcha?.reset) {
          (window as any).grecaptcha.reset();
        }
        (window as any).recaptchaToken = null;
      }

      resetCaptcha?.();
    } else {
      setNewPlate("");
    }
  }, [isModalOpen, isCaptchaMtcCaptcha, captchaName, resetCaptcha]);

  useEffect(() => {
    if (isModalOpen) {
      if (!hasInitializedCaptcha.current) {
        incrementCaptchaKey?.();
        hasInitializedCaptcha.current = true;
      }

      const timer = setTimeout(() => {
        setShowCaptcha(true);
        resetCaptcha?.();
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    } else {
      setShowCaptcha(false);
      hasInitializedCaptcha.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleOpenWebViewMenu = useCallback(() => {
    setIsWebViewMenuOpen(true);
  }, []);

  const handleCloseWebViewMenu = useCallback(() => {
    setIsWebViewMenuOpen(false);
  }, []);

  const showMenu = isWebView;

  const showPartnerPassagesResult = isDefaultFlow && items?.length > 0;

  const shouldBlur =
    isLoading ||
    (showAvulsoBackdrop && !!onAvulsoConfirm && !!onAvulsoDeny) ||
    (showNoDataBackdrop && !!onNoDataRegister && !!onNoDataDismiss) ||
    (showTagBackdrop && !!onTagContract && !!onTagDismiss) ||
    (showBeforeLeaveBackdrop &&
      !!onBeforeLeavePayNow &&
      !!onBeforeLeaveDismiss);

  return (
    <div className="relative flex flex-1 flex-col w-full min-h-screen bg-background">
      <div
        className={`flex flex-1 flex-col ${shouldBlur ? "blur-sm pointer-events-none select-none" : ""}`}
      >
        <header className="w-full bg-tertiary">
          <div className="relative flex items-center h-[75px] border-b border-white/10">
            <button
              onClick={showMenu ? handleOpenWebViewMenu : goGetout}
              type="button"
              className="bg-black flex items-center justify-center w-[75px] h-[75px] shrink-0"
              aria-label="Menu"
            >
              <img
                src="/svg/icon-close-white-2.0.svg"
                alt="menu"
                className="w-[22px] h-[22px]"
              />
            </button>
            <Icon className="dark ml-4" width="96" height="30" />
            <div className="ml-auto mr-3">
              <LanguageSelector className="text-white" showFlag={true} />
            </div>
          </div>
        </header>
        {isDefaultFlow ? (
          <div className="bg-tertiary">
            <div className="w-full flex justify-center">
              <div className="w-full max-w-[480px] px-4 pt-6 pb-16">
                <Alert variant="warning">{t("pages:home:warning48h")}</Alert>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-tertiary">
            <div className="w-full flex justify-center">
              <div className="w-full max-w-[480px] px-5 pt-4 pb-6">
                <p className="text-white text-sm font-secondary tracking-[-0.4px] leading-[1.12]">
                  {t("pages:home:yourVehicles")}
                </p>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={toggleModal}
                    className="w-full bg-white rounded-[16px] px-4 py-5 flex items-center gap-4 min-h-[88px] text-left"
                  >
                    <img
                      src="/svg/car-model.svg"
                      className="w-[72px] h-[44px] shrink-0"
                      alt="Ícone carro"
                    />
                    <div className="flex-1 flex flex-col gap-0.5 text-text tracking-[-0.4px]">
                      <p className="text-[20px] font-bold font-secondary leading-[1.2] uppercase">
                        {formattedPlate}
                      </p>
                    </div>
                    <img
                      src="/svg/icon-arrow-right-2.0.svg"
                      className="w-[9px] h-4 shrink-0"
                      alt=""
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Seção principal — passagens */}
        <main className="flex-1 bg-background pb-36">
          <div className="w-full flex justify-center">
            <div className="w-full max-w-[480px] px-4">
              {isDefaultFlow && (
                <div className="relative z-10 -mt-10">
                  <div className="w-full bg-foreground rounded-[16px] px-4 py-5 flex items-center gap-4 min-h-[88px]">
                    <img
                      src="/svg/car-model.svg"
                      alt=""
                      className="w-[72px] h-[44px] shrink-0"
                    />
                    <p className="text-[20px] font-bold font-secondary text-text tracking-[-0.4px] leading-[1.2] uppercase">
                      {formattedPlate}
                    </p>
                  </div>
                </div>
              )}

              {(items?.length > 0 || !isDefaultFlow) &&
                !showPartnerPassagesResult && (
                  <div className="pt-5">
                    <h2 className="text-base font-bold font-primary text-text tracking-[-0.1px]">
                      Passagens
                    </h2>
                    <div className="w-full h-px bg-border mt-4" />
                  </div>
                )}

              {items?.length > 0 && !showPartnerPassagesResult && (
                <>
                  <div
                    className={`flex items-center gap-4 py-4 ${
                      isLoading ? "opacity-50" : ""
                    }`}
                  >
                    <span
                      role="checkbox"
                      aria-checked={areAllSelectableDebtsSelected(
                        items,
                        selectables,
                      )}
                      aria-disabled={isLoading}
                      tabIndex={isLoading ? -1 : 0}
                      onClick={() => {
                        if (isLoading) return;
                        const syntheticEvent = {
                          target: {
                            name: "all",
                            checked: !areAllSelectableDebtsSelected(
                              items,
                              selectables,
                            ),
                          },
                        } as React.ChangeEvent<HTMLInputElement>;
                        onChange(syntheticEvent);
                      }}
                      onKeyDown={(e) => {
                        if (isLoading) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          const syntheticEvent = {
                            target: {
                              name: "all",
                              checked: !areAllSelectableDebtsSelected(
                                items,
                                selectables,
                              ),
                            },
                          } as React.ChangeEvent<HTMLInputElement>;
                          onChange(syntheticEvent);
                        }
                      }}
                      className={`w-6 h-6 rounded-full border-2 shrink-0 transition-colors ${
                        isLoading
                          ? "cursor-not-allowed pointer-events-none"
                          : "cursor-pointer"
                      } ${
                        areAllSelectableDebtsSelected(items, selectables)
                          ? "bg-primary border-primary"
                          : "border-[#696966] bg-transparent"
                      }`}
                    />
                    <PassagesCountStatus
                      label="Selecionar todos"
                      isSearching={isLoading}
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    {items?.map((item, index) => {
                      const isSelected =
                        selectables?.some(
                          (selectable) => selectable.name === item.id,
                        ) ?? false;
                      return (
                        <DebtPassageCard
                          key={item.id || index}
                          item={item}
                          isSelected={isSelected}
                          onChange={isLoading ? undefined : onChange}
                          formatPlate={(itemPlate) => itemPlate || plate}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              {showPartnerPassagesResult && (
                <PartnerPassagesResult
                  items={items}
                  selectables={selectables}
                  onChange={onChange}
                  plate={plate}
                  consultation={scrapersConsultation}
                  isSearching={isLoading}
                />
              )}

              {!isLoading && items?.length === 0 && isDefaultFlow && (
                  <EmptyConsultationResult
                    consultation={
                      scrapersConsultation ?? createPendingConsultation()
                    }
                    onSearchAnotherPlate={toggleModal}
                  />
                )}
            </div>
          </div>
        </main>
        {items?.length > 0 && (
          <OrgPayment disabled={isLoading} tracksAddToCart />
        )}
      </div>

      {isLoading && <Spinner />}

      {showAvulsoBackdrop && onAvulsoConfirm && onAvulsoDeny && (
        <SheetBackdrop
          onClose={onAvulsoDeny}
          overlayClassName="z-20 bg-black/30"
        >
          <div className="flex flex-col items-center shrink-0">
            <div className="w-10 h-1 bg-border rounded-full mb-6" />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin-internal flex flex-col items-center gap-6 w-full">
            <p className="text-sm text-center text-text leading-[18.2px]">
              Você tem{" "}
              <span className="font-bold">{avulsoPassageCount}</span>{" "}
              {avulsoPassageCount !== 1
                ? "passagens em aberto"
                : "passagem em aberto"}{" "}
              no
              <br />
              valor de{" "}
              <span className="font-bold">R$ {avulsoTotalValue}</span>
            </p>
            <div className="w-full h-px bg-border" />
            <p className="text-sm text-center text-text">
              Quer ver suas passagens?
            </p>
          </div>
          <div className="shrink-0 pt-5 w-full flex flex-col items-center gap-6">
            <button
              type="button"
              onClick={onAvulsoConfirm}
              className="w-full h-[calc(60px)] rounded-xl bg-primary flex items-center justify-center"
            >
              <span className="text-white font-bold text-base">Sim</span>
            </button>
            <button
              type="button"
              onClick={onAvulsoDeny}
              className="w-full h-[calc(60px)] rounded-xl flex items-center justify-center"
            >
              <span className="font-bold text-base text-text">Não</span>
            </button>
            <p className="text-xs text-center text-text tracking-[-0.4px]">
              <a href="/faq" className="underline">
                Consulte
              </a>{" "}
              as concessionárias parceiras
            </p>
          </div>
        </SheetBackdrop>
      )}
      {showNoDataBackdrop &&
        !isLoading &&
        items?.length === 0 &&
        onNoDataRegister &&
        onNoDataDismiss && (
          <SheetBackdrop
            onClose={onNoDataDismiss}
            overlayClassName="z-20 bg-black/30"
          >
            <div className="flex flex-col items-center shrink-0">
              <div className="w-10 h-1 bg-border rounded-full mb-6" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin-internal flex flex-col items-center gap-6 w-full">
              <p className="text-lg font-bold text-center text-text leading-[21.6px] tracking-[-0.1px] w-full">
                {t("pages:home:noPassagesTitle")}
              </p>
              <div className="w-full h-px bg-border" />
              <p className="text-sm text-center text-text leading-[18.2px]">
                {t("pages:home:noPassagesDescription")}
              </p>
            </div>
            <div className="shrink-0 pt-5 w-full flex flex-col items-center gap-6">
              <button
                type="button"
                onClick={onNoDataRegister}
                className="w-full h-[calc(60px)] rounded-xl bg-primary flex items-center justify-center"
              >
                <span className="text-white font-bold text-base">
                  {t("pages:home:noPassagesRegister")}
                </span>
              </button>
              <button
                type="button"
                onClick={onNoDataDismiss}
                className="w-full h-[calc(60px)] rounded-xl flex items-center justify-center"
              >
                <span className="font-bold text-base text-text">
                  {t("pages:home:noPassagesNotNow")}
                </span>
              </button>
              <p className="text-xs text-center text-text tracking-[-0.4px]">
                <a href="/faq" className="underline">
                  {t("pages:home:noPassagesConsult")}
                </a>
                {t("pages:home:noPassagesPartners")}
              </p>
            </div>
          </SheetBackdrop>
        )}

      {showTagBackdrop && onTagContract && onTagDismiss && (
        <SheetBackdrop
          onClose={onTagDismiss}
          overlayClassName="z-20 bg-black/30"
        >
          <div className="flex flex-col items-center shrink-0">
            <div className="w-10 h-1 bg-border rounded-full mb-6" />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin-internal flex flex-col items-center gap-6 w-full">
            <p className="text-lg font-bold text-center text-text leading-[21.6px] tracking-[-0.1px] w-full">
              {t("pages:home:tagBackdropTitle")}
            </p>
            <div className="w-full h-px bg-border" />
            <p className="text-sm text-center text-text leading-[18.2px]">
              {t("pages:home:tagBackdropDescription")}
            </p>
          </div>
          <div className="shrink-0 pt-5 w-full flex flex-col items-center gap-6">
            <button
              type="button"
              onClick={onTagContract}
              className="w-full h-[calc(60px)] rounded-xl bg-primary flex items-center justify-center"
            >
              <span className="text-white font-bold text-base">
                {t("pages:home:tagBackdropContract")}
              </span>
            </button>
            <button
              type="button"
              onClick={onTagDismiss}
              className="w-full h-[calc(60px)] rounded-xl flex items-center justify-center"
            >
              <span className="font-bold text-base text-text">
                {t("pages:home:tagBackdropNotNow")}
              </span>
            </button>
          </div>
        </SheetBackdrop>
      )}

      {showBeforeLeaveBackdrop &&
        onBeforeLeavePayNow &&
        onBeforeLeaveDismiss && (
          <SheetBackdrop
            onClose={onBeforeLeaveDismiss}
            overlayClassName="z-20 bg-black/30"
          >
            <div className="flex flex-col items-center shrink-0">
              <div className="w-10 h-1 bg-border rounded-full mb-6" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin-internal flex flex-col items-center gap-6 w-full">
              <p className="text-lg font-bold text-center text-text leading-[21.6px] tracking-[-0.1px] w-full">
                {t("pages:home:beforeLeaveTitle")}
              </p>
              <div className="w-full h-px bg-border" />
              <p className="text-sm text-center text-text leading-[18.2px]">
                {t("pages:home:beforeLeaveDescription")}
              </p>
            </div>
            <div className="shrink-0 pt-5 w-full flex flex-col items-center gap-6">
              <button
                type="button"
                onClick={onBeforeLeavePayNow}
                className="w-full h-[calc(60px)] rounded-xl bg-primary flex items-center justify-center"
              >
                <span className="text-white font-bold text-base">
                  {t("pages:home:beforeLeavePayNow")}
                </span>
              </button>
              <button
                type="button"
                onClick={onBeforeLeaveDismiss}
                className="w-full h-[calc(60px)] rounded-xl flex items-center justify-center"
              >
                <span className="font-bold text-base text-text">
                  {t("pages:home:beforeLeaveNotNow")}
                </span>
              </button>
            </div>
          </SheetBackdrop>
        )}

      {isModalOpen && (
        <Modal
          content={
            <div className="flex flex-col">
              <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-border rounded-full"></div>
              <button
                type="button"
                onClick={toggleModal}
                className="absolute top-6 right-6 text-text"
              >
                <img
                  src="/svg/icon-close.svg"
                  alt="Fechar"
                  className="w-6 h-6"
                />
              </button>
              <div className="pt-4">
                <h3 className="text-lg font-bold text-text mb-2">
                  {t("pages:home:newPlate")}
                </h3>
                <input
                  value={newPlate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const cleanPlate = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "");
                    setNewPlate(cleanPlate);
                  }}
                  placeholder={t("pages:home:enterPlate")}
                  minLength={5}
                  maxLength={7}
                  className="w-full text-base text-text bg-transparent border border-border p-3 rounded-lg h-14 mb-4 focus:outline-none focus:border-primary focus:border-2"
                  type="text"
                  autoFocus
                />
                {showCaptcha && (
                  <div className="w-full mb-4 flex justify-center relative">
                    <LoadingOverlay
                      isLoading={isLoadingCaptchaFlag}
                      bgColor="bg-white/50"
                    />
                    {isCaptchaMtcCaptcha ? (
                      <div
                        className="mtcaptcha"
                        id={captchaName}
                        data-callback="onCaptchaSuccess"
                      ></div>
                    ) : (
                      <div
                        className="g-recaptcha mx-auto"
                        data-sitekey={environment.recaptcha.siteKey}
                        data-size="normal"
                        data-theme="light"
                        data-callback="onRecaptchaVerify"
                        data-expired-callback="onRecaptchaExpired"
                        data-error-callback="onRecaptchaError"
                      />
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSearchClick}
                  disabled={isButtonDisabled}
                  className={buttonClassName}
                >
                  {t("pages:home:searchDebts")}
                </button>
              </div>
            </div>
          }
          actionClose={toggleModal}
        />
      )}
      <WebViewMenu
        isOpen={isWebViewMenuOpen}
        onClose={handleCloseWebViewMenu}
      />
    </div>
  );
}
